// Una función por ruta, con los tipos de @cotizador/compartido. Los cuerpos se validan AQUÍ con los mismos esquemas
// que usa el servidor (si algo no pasa, truena antes de salir y la persona ve qué campo falla).
import { CuerpoActualizarVersion,
  CuerpoMensajeSeguimiento,
  CuerpoResultadoSeguimiento,
  ConsultaBitacora,
  CuerpoActualizarCrm,
  CuerpoAgregarCosto,
  CuerpoAutorizarLectura,
  CuerpoCorregir,
  CuerpoSugerirConsumos,
  CuerpoEstimarConsumo,
  CuerpoCrearCotizacion,
  CuerpoEditarBorrador,
  CuerpoGenerarPdf,
  CuerpoGuardarVersion,
  CuerpoCascadaIA,
  CuerpoLlaveIA,
  CuerpoModeloIA,
  CuerpoPedirAprobacion,
  CuerpoPedirEnlace,
  CuerpoResolverAprobacion,
  CuerpoResolverAviso,
  CuerpoRevision,
  CuerpoTablaPrecios,
  CuerpoTopeIA,
  CuerpoUsuario,
  EntradaBitacora,
  Rol,
} from '@cotizador/compartido';
import type { RespuestaActualizarVersion,
  PlantillaSeguimiento,
  RespuestaHoyToca,
  RespuestaTablero,
  CuerpoResultadoSeguimiento as TipoCuerpoResultado,
  CascadaIA,
  ConsultaBitacora as TipoConsultaBitacora,
  Cotizacion,
  CuerpoCosto,
  CuerpoTablaPrecios as TipoCuerpoTablaPrecios,
  CuerpoUsuario as TipoCuerpoUsuario,
  Cuestionario,
  EdicionCampo,
  EstadoCotizacion,
  EstadoIA,
  IdModeloIA,
  Medidor,
  PiezasAmpliacion,
  RespuestaAprobacion,
  RespuestaArchivo,
  RespuestaBitacora,
  RespuestaConUso,
  RespuestaCorregir,
  ResultadoSugerirConsumos,
  RespuestaEstimarConsumo,
  RespuestaDetalle,
  RespuestaEventos,
  RespuestaListaCotizaciones,
  RespuestaSucursales,
  RespuestaPedirEnlace,
  RespuestaPrecios,
  RespuestaUsuario,
  RespuestaUsuarios,
  RespuestaVersion,
  RespuestaVerVersion,
  RespuestaYo,
  CuerpoVenta,
} from '@cotizador/compartido';
import { z } from 'zod';
import { ErrorDeApi, llamar } from './cliente.ts';

/** Valida un cuerpo con su esquema; si no pasa, ErrorDeApi DATOS_INVALIDOS con cada motivo (no sale nada al servidor). */
function validar<S extends z.ZodType>(esquema: S, valor: unknown): z.infer<S> {
  const r = esquema.safeParse(valor);
  if (!r.success)
    throw new ErrorDeApi(
      'DATOS_INVALIDOS',
      'Hay datos que no sirven. Revisa lo marcado.',
      r.error.issues.map((i) => `${i.path.join('.') || 'cuerpo'}: ${i.message}`),
    );
  return r.data;
}

/** Respuesta de la bitácora del sistema: se revisa al llegar. Una entrada con otra forma (una acción que la interfaz
 *  no conoce, sin fecha…) truena aquí en voz alta en vez de pintarse a medias. */
const RespuestaBitacoraEsquema = z.object({ entradas: z.array(EntradaBitacora) });

function revisarRespuesta<S extends z.ZodType>(esquema: S, valor: unknown, que: string): z.infer<S> {
  const r = esquema.safeParse(valor);
  if (!r.success)
    throw new ErrorDeApi(
      'RESPUESTA_RARA',
      `El servidor mandó ${que} con una forma que la interfaz no conoce.`,
      r.error.issues.slice(0, 8).map((i) => `${i.path.join('.') || 'respuesta'}: ${i.message}`),
    );
  return r.data;
}

/** Quita los filtros vacíos ("" o null) antes de validar la consulta (el esquema es estricto). */
function sinVacios<T extends Record<string, unknown>>(o: T): Partial<T> {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== '')) as Partial<T>;
}

/** Algunas rutas devuelven la cotización actualizada; si una no la trae, la interfaz recarga el detalle. */
export function cotizacionDe(r: unknown): Cotizacion | null {
  if (r && typeof r === 'object' && 'cotizacion' in r) {
    const c = (r as { cotizacion: unknown }).cotizacion;
    if (c && typeof c === 'object' && 'revision' in c && 'borrador' in c) return c as Cotizacion;
  }
  return null;
}

// Todas son async: un cuerpo que no sirve llega como promesa rechazada, igual que un error del servidor.
export const api = {
  yo: async () => llamar<RespuestaYo>('yo'),
  pedirEnlace: async (correo: string) => llamar<RespuestaPedirEnlace>('pedirEnlace', { cuerpo: validar(CuerpoPedirEnlace, { correo }) }),
  entrarConToken: async (token: string) => llamar<unknown>('entrar', { consulta: { token } }),
  entrarFirebase: async (id_token: string) => llamar<unknown>('entrarFirebase', { cuerpo: { id_token } }),
  salir: async () => llamar<unknown>('salir', { cuerpo: {} }),

  usuarios: async () => llamar<RespuestaUsuarios>('listarUsuarios'),
  crearUsuario: async (u: TipoCuerpoUsuario) => llamar<RespuestaUsuario>('crearUsuario', { cuerpo: validar(CuerpoUsuario, u) }),
  // Al editar se acepta el rol que ya tenía (un técnico de antes de la A0b se puede desactivar); el servidor no deja PASAR a técnico.
  editarUsuario: async (id: string, u: Omit<TipoCuerpoUsuario, 'rol'> & { rol: Rol }) =>
    llamar<RespuestaUsuario>('editarUsuario', { params: { id }, cuerpo: validar(CuerpoUsuario.extend({ rol: Rol }), u) }),

  /** `papelera: true` = SOLO las de la papelera (solo Diego; el servidor responde 403 a los demás). Sin él, la lista
   *  nunca trae las de la papelera. */
  cotizaciones: async (f: { estado?: EstadoCotizacion | ''; vendedor_id?: string; texto?: string; desde?: string; hasta?: string; limite?: number; sin_respuesta?: boolean; papelera?: boolean }) => {
    const { sin_respuesta, papelera, ...resto } = f;
    return llamar<RespuestaListaCotizaciones>('listarCotizaciones', {
      consulta: { ...resto, limite: f.limite ?? 500, sin_respuesta: sin_respuesta ? 'si' : undefined, papelera: papelera ? 'si' : undefined },
    });
  },
  /** MULTISITIO: `multisitio` = esta cotización es una sucursal (junto_a = otra sucursal del mismo proyecto, o null la primera). */
  /** `piezas`: la captura con que nace una ampliación de baterías (la batería y las cantidades; lo demás, en su pantalla). */
  crearCotizacion: async (cuestionario: Cuestionario, vendedor_id: string | null, multisitio?: { sitio: string; junto_a: string | null }, piezas?: PiezasAmpliacion) =>
    llamar<{ cotizacion: Cotizacion }>('crearCotizacion', { cuerpo: validar(CuerpoCrearCotizacion, { cuestionario, vendedor_id, ...(multisitio ? { multisitio } : {}), ...(piezas ? { piezas } : {}) }) }),
  detalle: async (id: string) => llamar<RespuestaDetalle>('detalleCotizacion', { params: { id } }),
  /** Nube, tanda 2 · historial paginado: los eventos ANTERIORES al cursor `antes` (del más viejo al más nuevo). */
  historial: async (id: string, antes: number, limite: number) => llamar<RespuestaEventos>('historialCotizacion', { params: { id }, consulta: { antes, limite } }),
  editarBorrador: async (id: string, revision: number, ediciones: EdicionCampo[]) =>
    llamar<{ cotizacion: Cotizacion }>('editarBorrador', { params: { id }, cuerpo: validar(CuerpoEditarBorrador, { revision, ediciones }) }),
  /** «Hoy toca»: sin `vendedor_id`, la lista propia; Diego puede pedir la de otro o «todos». */
  hoyToca: async (vendedor_id?: string) => llamar<RespuestaHoyToca>('hoyToca', { consulta: vendedor_id ? { vendedor_id } : {} }),
  tableroSeguimiento: async () => llamar<RespuestaTablero>('tableroSeguimiento'),
  /** keepalive: se manda justo cuando el teléfono se pasa a WhatsApp; sin él, el iPhone puede cortar la petición y el mensaje no se anota. */
  mensajeSeguimiento: async (id: string, plantilla: PlantillaSeguimiento, yaLoMande = false) => llamar<{ cotizacion: Cotizacion }>('mensajeSeguimiento', { params: { id }, cuerpo: validar(CuerpoMensajeSeguimiento, { plantilla, ...(yaLoMande ? { ya_lo_mande: true } : {}) }), keepalive: true }),
  resultadoSeguimiento: async (id: string, cuerpo: TipoCuerpoResultado) => llamar<{ cotizacion: Cotizacion; tambien: string[]; parecidos: string[] }>('resultadoSeguimiento', { params: { id }, cuerpo: validar(CuerpoResultadoSeguimiento, cuerpo) }),
  actualizarCrm: async (
    id: string,
    cambios: { revision: number; estado?: EstadoCotizacion; vendedor_id?: string; notas_crm?: string | null; proximo_seguimiento?: string | null; venta?: CuerpoVenta },
  ) => llamar<{ cotizacion: Cotizacion }>('actualizarCrm', { params: { id }, cuerpo: validar(CuerpoActualizarCrm, cambios) }),
  subirArchivo: async (id: string, revision: number, archivo: File, medidor: Medidor, clase: 'recibo' | 'finsus' | 'foto_sitio' | 'otro') => {
    const f = new FormData();
    // Los campos de texto van ANTES del archivo: el servidor los lee en orden al recibir el multipart.
    f.append('revision', String(revision));
    f.append('medidor', medidor);
    f.append('clase', clase);
    f.append('archivo', archivo, archivo.name);
    return llamar<RespuestaArchivo>('subirArchivo', { params: { id }, formulario: f });
  },
  quitarArchivo: async (id: string, archivoId: string, revision: number) =>
    llamar<{ cotizacion: Cotizacion }>('quitarArchivo', { params: { id, archivoId }, consulta: { revision } }),
  leerRecibos: async (id: string, revision: number) => llamar<RespuestaConUso>('leerRecibos', { params: { id }, cuerpo: { revision } }),
  /** Etapa 4: el vendedor revisó la tabla leída (con sus correcciones, si hay) y la AUTORIZA: se calculan las opciones. */
  autorizarLectura: async (id: string, revision: number, medidor: Medidor, ediciones: EdicionCampo[] = []) =>
    llamar<{ cotizacion: Cotizacion }>('autorizarLectura', { params: { id, medidor }, cuerpo: validar(CuerpoAutorizarLectura, { revision, ediciones }) }),
  /** z11: cliente nuevo de casa o negocio: de la descripción del vendedor al cuadro de cargas (IA de «corregir»). */
  estimarConsumo: async (descripcion: string, tipo: 'casa' | 'comercio') =>
    llamar<RespuestaEstimarConsumo>('estimarConsumo', { cuerpo: validar(CuerpoEstimarConsumo, { descripcion, tipo }) }),
  /** ADENDA z52-2: cuánto SUBE el consumo de quien ya tiene recibo (solo los equipos nuevos); el costo va a la bitácora de la cotización. */
  estimarAumento: async (descripcion: string, tipo: 'casa' | 'comercio', giro: string | null, cotizacionId: string | null) =>
    llamar<RespuestaEstimarConsumo>('estimarConsumo', { cuerpo: validar(CuerpoEstimarConsumo, { descripcion, tipo, modo: 'aumento', giro, ...(cotizacionId ? { cotizacion_id: cotizacionId } : {}) }) }),
  /** hoy86 · z64-2: «Sugerir con IA» en un negocio PDBT: la IA sugiere los periodos sin recibo (no guarda nada). `senal` corta la
   *  espera (la tabla pone la curva con aviso si la IA tarda). */
  sugerirConsumos: async (id: string, cuerpo: z.input<typeof CuerpoSugerirConsumos>, senal?: AbortSignal) =>
    llamar<ResultadoSugerirConsumos>('sugerirConsumos', { params: { id }, cuerpo: validar(CuerpoSugerirConsumos, cuerpo), ...(senal ? { senal } : {}) }),
  corregir: async (id: string, revision: number, instruccion: string) =>
    llamar<RespuestaCorregir>('corregirConIA', { params: { id }, cuerpo: validar(CuerpoCorregir, { revision, instruccion }) }),
  agregarCosto: async (id: string, revision: number, costo: CuerpoCosto) =>
    llamar<{ cotizacion: Cotizacion }>('agregarCosto', { params: { id }, cuerpo: validar(CuerpoAgregarCosto, { revision, costo }) }),
  quitarCosto: async (id: string, costoId: string, revision: number) =>
    llamar<{ cotizacion: Cotizacion }>('quitarCosto', { params: { id, costoId }, consulta: { revision } }),
  calcular: async (id: string, revision: number) => llamar<{ cotizacion: Cotizacion }>('calcular', { params: { id }, cuerpo: { revision } }),
  /** OPTIMUS (Diego, 12-oct noche): «Aplicar el precio de…»: el precio que le toca al vendedor de hoy (nunca cambia solo). */
  aplicarPrecioVendedor: async (id: string, revision: number) => llamar<{ cotizacion: Cotizacion }>('aplicarPrecioVendedor', { params: { id }, cuerpo: { revision } }),
  /** ADENDA z48-8: «Actualizar a la versión nueva»: en seco (confirmar=false) no guarda nada; al confirmar trae la cotización. */
  actualizarVersion: async (id: string, revision: number, confirmar: boolean) =>
    llamar<RespuestaActualizarVersion>('actualizarVersion', { params: { id }, cuerpo: validar(CuerpoActualizarVersion, { revision, confirmar }) }),
  revisarConIA: async (id: string, revision: number) => llamar<RespuestaConUso>('revisarConIA', { params: { id }, cuerpo: { revision } }),
  resolverAviso: async (id: string, avisoId: string, revision: number, nota: string) =>
    llamar<{ cotizacion: Cotizacion }>('resolverAviso', { params: { id, avisoId }, cuerpo: validar(CuerpoResolverAviso, { revision, nota }) }),
  guardarVersion: async (id: string, revision: number, motivo: string) =>
    llamar<RespuestaVersion>('guardarVersion', { params: { id }, cuerpo: validar(CuerpoGuardarVersion, { revision, motivo }) }),
  verVersion: async (id: string, numero: number) => llamar<RespuestaVerVersion>('verVersion', { params: { id, numero } }),
  generarPdf: async (id: string, revision: number, motivo: string, confirmar: { aviso_id: string; nota: string }[]) =>
    llamar<RespuestaVersion>('generarPdf', {
      params: { id },
      cuerpo: validar(CuerpoGenerarPdf, { revision, motivo, confirmar_avisos_rojos: confirmar }),
    }),
  /** Diego, 13-oct: el PDF de TODAS las sucursales del proyecto (queda como versión de la sucursal principal). */
  generarPdfSucursales: async (id: string) => llamar<RespuestaVersion>('generarPdfSucursales', { params: { id } }),
  /** MULTISITIO · la franja: las sucursales del proyecto de esta cotización (sin la papelera), por folio. No es la lista
   *  del CRM: sigue respondiendo con el fusible de listas saltado. */
  sucursales: async (id: string) => llamar<RespuestaSucursales>('sucursalesProyecto', { params: { id } }),
  /** z65 · «Separar en otro sitio» (dominio/sitios-proyecto.ts del servidor). */
  separarSitio: async (id: string, cuerpo: { revision: number; sitio_actual: string; sitio_nuevo: string; cuestionario: Cuestionario; archivo_ids: string[] }) =>
    llamar<{ original: Cotizacion; nueva: Cotizacion }>('separarSitio', { params: { id }, cuerpo: cuerpo }),
  /** ADENDA A0b · pedir el OK de Diego (descuento mayor al tope, debajo del piso o del margen mínimo). */
  pedirAprobacion: async (id: string, revision: number, nota: string | null) =>
    llamar<RespuestaAprobacion>('pedirAprobacion', { params: { id }, cuerpo: validar(CuerpoPedirAprobacion, { revision, nota }) }),
  /** ADENDA A0b · Diego aprueba o rechaza los precios que está viendo. */
  resolverAprobacion: async (id: string, revision: number, aprobar: boolean, nota: string | null) =>
    llamar<{ cotizacion: Cotizacion }>('resolverAprobacion', { params: { id }, cuerpo: validar(CuerpoResolverAprobacion, { revision, aprobar, nota }) }),
  /** PAPELERA (Diego, 13-oct): la manda a la papelera (recuperable). Diego, cualquiera; un vendedor, solo las suyas en Borrador. */
  mandarAPapelera: async (id: string, revision: number) =>
    llamar<{ cotizacion: Cotizacion }>('mandarAPapelera', { params: { id }, cuerpo: validar(CuerpoRevision, { revision }) }),
  /** PAPELERA: Diego la saca de la papelera y vuelve como estaba. */
  recuperarCotizacion: async (id: string, revision: number) =>
    llamar<{ cotizacion: Cotizacion }>('recuperarCotizacion', { params: { id }, cuerpo: validar(CuerpoRevision, { revision }) }),
  enviarCrmExterno: async (id: string) => llamar<unknown>('enviarCrmExterno', { params: { id }, cuerpo: {} }),

  precios: async () => llamar<RespuestaPrecios>('verPrecios'),
  guardarPrecios: async (t: TipoCuerpoTablaPrecios) => llamar<unknown>('guardarPrecios', { cuerpo: validar(CuerpoTablaPrecios, t) }),

  estadoIA: async () => llamar<EstadoIA>('estadoIA'),
  guardarLlaveIA: async (llave: string) => llamar<unknown>('guardarLlaveIA', { cuerpo: validar(CuerpoLlaveIA, { llave }) }),
  borrarLlaveIA: async () => llamar<unknown>('borrarLlaveIA'),
  guardarTopeIA: async (tope_mes_usd: number | null) => llamar<unknown>('guardarTopeIA', { cuerpo: validar(CuerpoTopeIA, { tope_mes_usd }) }),
  /** Contrato v2 · null = volver al modelo por omisión. Responde el EstadoIA nuevo. */
  guardarModeloIA: async (modelo: IdModeloIA | null) => llamar<EstadoIA>('guardarModeloIA', { cuerpo: validar(CuerpoModeloIA, { modelo }) }),
  guardarCascadaIA: async (cascada: CascadaIA) => llamar<EstadoIA>('guardarCascadaIA', { cuerpo: validar(CuerpoCascadaIA, cascada) }),

  /** Contrato v2 · bitácora del sistema (solo director), más reciente primero. */
  bitacora: async (f: { [K in keyof TipoConsultaBitacora]?: TipoConsultaBitacora[K] | '' | null }): Promise<RespuestaBitacora> => {
    const consulta = validar(ConsultaBitacora, sinVacios(f));
    const r = await llamar<unknown>('verBitacora', { consulta });
    return revisarRespuesta(RespuestaBitacoraEsquema, r, 'la bitácora');
  },
};
